import { currenciesSymbol } from '@/helpers/formatCurrency';
import type { AdditionalAccountWallet } from '../types/additionalAccount.type';
import type {
  CreateAdditionalAccountPayload,
  CreateAdditionalAccountResponse,
} from '../types/createAdditionalAccount.type';

/**
 * Construye la `AdditionalAccountWallet` de la cuenta recién creada (ML-3)
 * combinando la respuesta del backend con los datos que solo conoce el drawer.
 *
 * Qué manda cada lado, y por qué:
 * - **Backend**: `accountId` (pasa a ser el `walletId` con el que se navega al
 *   detalle), `name` y `currencyCode` — si el backend normalizó el nombre, gana
 *   su versión, no la tipeada.
 * - **Payload**: `countryCode`/`flagCountryCode` — la respuesta no los trae.
 *
 * El saldo se toma del payload SOLO si el fondeo ocurrió: con `FAILED` la
 * cuenta existe y el dinero no se movió, así que mostrar el monto pedido sería
 * afirmar un saldo inexistente. `NOT_REQUESTED` es el camino "Cargar más tarde".
 *
 * `accountNumber`/`bankAccounts` quedan vacíos a propósito: el create no
 * devuelve el número de cuenta (solo si lo tendrá, vía `hasOwnAccountNumber`)
 * y llega recién en el siguiente `GET` de `grouped-by-currency`. `accountBranchName` también
 * queda vacío — el `branch` de la respuesta es el del core bank
 * (`B2B_GLOBAL_81`), no el nombre que consumen los flujos de conversión.
 */
export function buildCreatedAdditionalAccountWallet(
  payload: CreateAdditionalAccountPayload,
  response: CreateAdditionalAccountResponse,
  existingAccountsCount: number
): AdditionalAccountWallet {
  const currency = response.currencyCode || payload.currency;
  const currencySymbol = currenciesSymbol[currency as keyof typeof currenciesSymbol] ?? '';
  const fundedAmount = response.initialFunding === 'FUNDED' ? payload.amount : 0;

  return {
    accountName: response.name || payload.accountName,
    wallet: {
      walletId: response.accountId,
      createdDate: new Date().toISOString(),
      accountBranchName: '',
      currency,
      countryCode: payload.countryCode,
      flagCountryCode: payload.flagCountryCode,
      isMainAccount: false,
      balance: {
        availableBalance: fundedAmount,
        totalBalance: fundedAmount,
        lockedBalance: 0,
        currency,
        countryCode: payload.countryCode,
        currencySymbol,
        dateMillis: 0,
      },
      checkDataAccount: '',
      // `hasOwnAccountNumber: false` (caso Brasil) = la cuenta no puede ser
      // origen de un PayOut, que es exactamente lo que gatea este flag.
      withdrawalAvailable: response.hasOwnAccountNumber,
      accountNumber: 0,
      bankAccountCode: null,
      bankAccounts: [],
      routeId: existingAccountsCount + 1,
      compensationNumbers: [],
    },
  };
}
